import { Component, HostListener, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { LanguageService } from './services/language.service';
import { SessionToken } from './components/verify-email/verify-email.component';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '@auth0/auth0-angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, switchMap, take } from 'rxjs/operators';
import { SNACKBAR_CLOSE } from './utils/snackbar';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  isLandingPage = false;
  state: string;
  sessionData: SessionToken | null = null;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private languageService: LanguageService,
    private renderer: Renderer2,
    private auth: AuthService,
    private snackBar: MatSnackBar
  ) {
    // Available languages
    translate.addLangs(['de', 'fr']);
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('de');
    // Check localStorage for saved language
    this.languageService.initLanguage();
  }

  ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (
          this.router.url === '/about-fenaco-login' ||
          this.router.url === '/imprint' ||
          this.router.url === '/privacy'
        ) {
          this.isLandingPage = true;
        } else {
          this.isLandingPage = false;
        }
      }
    });

    this.route.queryParams.subscribe((params) => {
      const sessionToken = params['session_token'];
      if (sessionToken) {
        this.state = params['state'];
        this.sessionData = this.decodeToken(sessionToken);
      }
      this.languageService.setLanguage(
        this.sessionData?.locale || this.languageService.getCurrentLanguage()
      );
    });

    this.showProfileUpdated();
    this.checkScreenSizeAndScroll();
  }

  // Back from the Benutzerkonto Form (AccountComponent editAccount()): the success message
  // on every return (CIAM-418). The Form may have changed the contact language, so switch
  // to it first; translate.get() waits for a language that is still loading.
  showProfileUpdated() {
    this.auth.appState$
      .pipe(
        filter((appState) => appState?.profileForm === 'account'),
        switchMap(() => this.auth.user$.pipe(filter((user) => !!user), take(1))),
        switchMap((user) => {
          const profile = user['com.fenaco.id/profile'];
          if (profile) {
            this.languageService.setLanguage(profile.locale);
          }
          return this.translate.get('editProfile.feedbacks.profileUpdated');
        })
      )
      .subscribe((text: string) => {
        this.snackBar.open(text, SNACKBAR_CLOSE, {
          duration: 3000,
          verticalPosition: 'top',
          politeness: 'assertive',
          panelClass: ['success-snackbar'],
        });
      });
  }

  decodeToken(token: string) {
    try {
      return jwtDecode<SessionToken>(token);
    } catch (Error) {
      return null;
    }
  }

  @HostListener('window:scroll')
  checkScreenSizeAndScroll() {
    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const element = document.getElementById('#navbar');
    const section = document.getElementById('#scroll-section');

    // Add or remove the class based on scroll position
    if (element && section) {
      if (scrollPosition > 0) {
        this.renderer.addClass(element, 'scrolled-down');
        const height = element.getBoundingClientRect().height;
        section.style.paddingTop = `${height}px`;
      } else {
        this.renderer.removeClass(element, 'scrolled-down');
        section.style.paddingTop = '0px';
      }
    }
  }
}
